import { IMPORT_STEP_MS, PLATFORM_LABEL, type Platform } from '@str/shared';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Logo, Spinner } from '@/components/ui';
import { errorMessage } from '@/lib/api';
import { useImport } from '@/lib/queries';
import { rememberActiveProperty } from '@/lib/property-store';
import { mediaStyle } from '@/lib/scenes';

/**
 * The server reports completed stages as the job runs; the screen never shows more than one
 * new stage per IMPORT_STEP_MS so fast imports still read as a sequence.
 */
function usePacedStep(serverStep: number, stepCount: number) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (shown >= Math.min(serverStep, stepCount)) return;
    const t = window.setTimeout(() => setShown((s) => s + 1), IMPORT_STEP_MS);
    return () => window.clearTimeout(t);
  }, [shown, serverStep, stepCount]);
  return shown;
}

export function Importing() {
  const { importId } = useParams();
  const navigate = useNavigate();
  const q = useImport(importId);
  const p = q.data;
  const step = usePacedStep(p?.step ?? 0, p?.stepCount ?? 5);
  const finished = !!p?.done && !p.failed && step >= (p.stepCount ?? 5);

  useEffect(() => {
    if (!finished || !p) return;
    rememberActiveProperty(p.propertyId);
    const t = window.setTimeout(
      () => void navigate(`/review/${p.propertyId}`, { replace: true }),
      900,
    );
    return () => window.clearTimeout(t);
  }, [finished, p, navigate]);

  if (q.error) {
    return (
      <div className="screen">
        <div className="topbar">
          <Logo />
        </div>
        <div style={{ padding: 40 }} className="err">
          {errorMessage(q.error)}
        </div>
      </div>
    );
  }
  if (p?.failed) {
    return (
      <div className="screen">
        <div className="topbar">
          <Logo />
        </div>
        <div
          className="a-rise-5"
          style={{
            maxWidth: 560,
            margin: '60px auto',
            padding: '0 40px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <h1 style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-.035em', lineHeight: 1.05 }}>
            We couldn&apos;t read that listing.
          </h1>
          <p style={{ fontSize: 15.5, color: '#666', lineHeight: 1.5 }} role="alert">
            {p.error?.message ?? 'Try your Airbnb or direct booking link instead.'}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/" className="btn btn-primary btn-lg hv">
              Try another link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const platform = PLATFORM_LABEL[(p?.platform ?? 'airbnb') as Platform];
  const steps = p?.steps ?? [];
  const pct = Math.round(8 + step * 18.4);

  return (
    <div className="screen importing-page">
      <div className="topbar">
        <Logo />
        <div
          className="importing-status"
          style={{ fontSize: 12.5, color: '#777', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <Spinner />
          Importing from {platform}
        </div>
      </div>
      <div
        className="grid-md-1 importing-body"
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '460px 1fr',
          gap: 60,
          padding: '0 100px 60px',
          alignItems: 'center',
          maxWidth: 1280,
          width: '100%',
          margin: '0 auto',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h1
              style={{ fontSize: 38, lineHeight: 1.05, fontWeight: 800, letterSpacing: '-.035em' }}
            >
              Reading {p?.property.name ?? 'your listing'}…
            </h1>
            <p style={{ fontSize: 15, color: '#666' }}>
              {p
                ? [
                    p.property.location,
                    p.property.listingType,
                    p.property.rating
                      ? `${p.property.rating.toFixed(2)} ★ (${p.property.reviewCount})`
                      : p.property.reviewCount
                        ? `${p.property.reviewCount} reviews`
                        : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : ' '}
            </p>
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 99,
              background: 'rgba(0,0,0,.08)',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                display: 'block',
                width: `${pct}%`,
                height: '100%',
                borderRadius: 99,
                background: '#111',
                transition: 'width .8s cubic-bezier(.2,.7,.2,1)',
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {steps.map((s, i) => {
              const st = i < step ? 'done' : i === step ? 'active' : 'todo';
              return (
                <div
                  key={s.title}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '12px 0',
                    borderBottom: '1px solid rgba(0,0,0,.06)',
                    opacity: st === 'todo' ? 0.45 : 1,
                    transition: 'opacity .4s',
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 99,
                      border: `1.5px solid ${st === 'done' ? '#111' : st === 'active' ? 'var(--accent)' : 'rgba(0,0,0,.18)'}`,
                      background: st === 'done' ? '#111' : 'transparent',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 'none',
                      transition: 'all .3s',
                    }}
                  >
                    {st === 'done' ? '✓' : ''}
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{s.title}</span>
                    <span style={{ fontSize: 13, color: '#777' }}>{s.subtitle}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div
          className="hide-md"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3,1fr)',
            gap: 12,
            alignContent: 'center',
          }}
        >
          {(p?.photos ?? []).length === 0 && <PhotoPlaceholders />}
          {(p?.photos ?? []).map((ph, i) => {
            const on = i < step * 1.5;
            return (
              <div
                key={ph.id}
                style={{
                  aspectRatio: '1',
                  borderRadius: 16,
                  ...mediaStyle(ph),
                  boxShadow: 'inset 0 -60px 70px rgba(0,0,0,.22),0 10px 30px -14px rgba(0,0,0,.3)',
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  padding: 10,
                  opacity: on ? 1 : 0,
                  transform: on ? 'none' : 'scale(.9) translateY(12px)',
                  transition: 'opacity .5s ease,transform .6s cubic-bezier(.2,.8,.2,1)',
                }}
              >
                <span className="kbd" style={{ color: 'rgba(255,255,255,.85)' }}>
                  {ph.label}
                </span>
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 99,
                    background: '#fff',
                    color: '#111',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ✓
                </span>
              </div>
            );
          })}
          <div
            style={{
              gridColumn: '1/-1',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginTop: 6,
              opacity: step >= 3 ? 1 : 0,
              transition: 'opacity .5s',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: '#777',
              }}
            >
              <span style={{ fontWeight: 600 }}>Reading reviews</span>
              <span>
                {p?.reviewsRead ?? 0} / {p?.property.reviewCount ?? 0}
              </span>
            </div>
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 12,
                background: '#fff',
                border: '1px solid rgba(0,0,0,.07)',
                fontSize: 13,
                color: '#333',
                lineHeight: 1.45,
              }}
            >
              {p?.quote ? `“${p.quote}”` : 'Looking for the most quotable lines…'}
              <span
                style={{
                  display: 'inline-block',
                  width: 2,
                  height: 13,
                  background: '#111',
                  marginLeft: 3,
                  verticalAlign: -2,
                  animation: 'blink 1s infinite',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Before the first photo lands the right column would be empty; six soft tiles breathe in
 * sequence so the wait reads as "about to happen" rather than "nothing is happening".
 */
function PhotoPlaceholders() {
  return (
    <>
      {Array.from({ length: 6 }, (_, i) => (
        <div
          key={i}
          data-testid="photo-placeholder"
          aria-hidden
          style={{
            aspectRatio: '1',
            borderRadius: 16,
            background:
              'linear-gradient(110deg, rgba(0,0,0,.045) 30%, rgba(0,0,0,.09) 50%, rgba(0,0,0,.045) 70%)',
            backgroundSize: '200% 100%',
            animation: `shimmer 1.8s ease-in-out infinite, rise .6s both`,
            animationDelay: `${i * 0.12}s, ${i * 0.06}s`,
          }}
        />
      ))}
      <div
        aria-live="polite"
        style={{
          gridColumn: '1/-1',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12.5,
          color: '#888',
          animation: 'fade .8s both',
          animationDelay: '.3s',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 99,
            background: 'var(--accent)',
            animation: 'pulse 1.6s ease-out infinite',
            flex: 'none',
          }}
        />
        Photos land here as we save them.
      </div>
    </>
  );
}
